import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaClock, FaTag } from "react-icons/fa";
import { DESTINATIONS, ORIGIN } from "./Globe/destinations";
import { supportsWebGL } from "./Globe/webgl";
import "../Styles/Globe.css";

// three.js is ~600KB — keep it out of the main bundle and off the critical path.
const GlobeScene = lazy(() => import("./Globe/GlobeScene"));

export default function DestinationGlobe() {
  const [activeId, setActiveId] = useState(null);
  const [visible, setVisible] = useState(false);
  const [canRender, setCanRender] = useState(null); // null = not yet decided
  const [reduced, setReduced] = useState(false);

  const sectionRef = useRef(null);
  const labelEls = useRef({});

  const active = DESTINATIONS.find((d) => d.id === activeId) || null;

  useEffect(() => {
    setCanRender(supportsWebGL());
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // Only run the render loop while the globe is actually on screen.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { rootMargin: "200px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const setLabelRef = useCallback(
    (id) => (el) => {
      if (el) labelEls.current[id] = el;
      else delete labelEls.current[id];
    },
    []
  );

  return (
    <section className="ve-section ve-section--ink ve-globe-section" ref={sectionRef}>
      <div className="ve-globe-section__grain" aria-hidden="true" />

      <div className="ve-container">
        <div className="ve-section-head">
          <span className="ve-eyebrow">Where we send people</span>
          <h2 className="ve-h2">
            Six countries. One <span className="ve-serif">departure gate.</span>
          </h2>
          <p className="ve-lede">
            Every route below starts in the same room in Andheri West. Pick a destination
            to see what the path actually looks like — how long it takes, what it costs,
            and which visa carries you there.
          </p>
        </div>

        <div className="ve-globe-layout">
          {/* ---------------- Globe ---------------- */}
          <div className="ve-globe">
            {canRender === true && (
              <Suspense fallback={<GlobeSkeleton />}>
                <GlobeScene
                  activeId={activeId}
                  labelEls={labelEls}
                  running={visible}
                  autoSpin={!reduced}
                />
              </Suspense>
            )}

            {canRender === false && <GlobeFallback />}
            {canRender === null && <GlobeSkeleton />}

            {/* Labels live in the DOM so they stay crisp and selectable; the
                scene positions them each frame. */}
            {canRender === true && (
              <div className="ve-globe__labels" aria-hidden="true">
                {DESTINATIONS.map((d) => (
                  <button
                    key={d.id}
                    ref={setLabelRef(d.id)}
                    type="button"
                    tabIndex={-1}
                    className={`ve-globe__label ${activeId === d.id ? "is-active" : ""}`}
                    onMouseEnter={() => setActiveId(d.id)}
                    onClick={() => setActiveId(d.id)}
                  >
                    {d.country}
                  </button>
                ))}
              </div>
            )}

            <div className="ve-globe__origin">
              <span className="ve-globe__origin-dot" />
              Departing {ORIGIN.label}
            </div>
          </div>

          {/* ---------------- Destination list ---------------- */}
          <div className="ve-globe-list">
            <ul className="ve-dest-list">
              {DESTINATIONS.map((d) => {
                const isActive = activeId === d.id;
                return (
                  <li key={d.id}>
                    <button
                      type="button"
                      className={`ve-dest-row ${isActive ? "is-active" : ""}`}
                      onMouseEnter={() => setActiveId(d.id)}
                      onFocus={() => setActiveId(d.id)}
                      onClick={() => setActiveId(isActive ? null : d.id)}
                      aria-expanded={isActive}
                    >
                      <span className="ve-dest-row__code">{d.code}</span>
                      <span className="ve-dest-row__main">
                        <span className="ve-dest-row__country">{d.country}</span>
                        <span className="ve-dest-row__route">{d.route}</span>
                      </span>
                      <FaArrowRight className="ve-dest-row__chev" />
                    </button>

                    <div className={`ve-dest-panel ${isActive ? "is-open" : ""}`}>
                      <div className="ve-dest-panel__inner">
                        <h3 className="ve-h4">{d.headline}</h3>
                        <p>{d.detail}</p>
                        <div className="ve-dest-panel__meta">
                          <span className="ve-pill">
                            <FaClock /> {d.time}
                          </span>
                          <span className="ve-pill">
                            <FaTag /> from {d.from}
                          </span>
                        </div>
                        <Link to={d.link} className="ve-link">
                          See the {d.country} pathway <FaArrowRight />
                        </Link>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            <p className="ve-globe-hint">
              Drag the globe to spin it. Hover a country to fly the route.
            </p>
          </div>
        </div>
      </div>

      {/* Screen readers get the same information as a plain list. */}
      <div className="ve-sr-only">
        <h3>Destinations we handle</h3>
        <ul>
          {DESTINATIONS.map((d) => (
            <li key={d.id}>
              <Link to={d.link}>
                {d.country} — {d.route}. {d.detail} Typical timeline {d.time}, from {d.from}.
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <span className="ve-sr-only" aria-live="polite">
        {active ? `${active.country}: ${active.headline}` : ""}
      </span>
    </section>
  );
}

function GlobeSkeleton() {
  return (
    <div className="ve-globe__skeleton" aria-hidden="true">
      <div className="ve-globe__skeleton-ring" />
    </div>
  );
}

/** Shown when WebGL is unavailable — still communicates the same idea. */
function GlobeFallback() {
  return (
    <div className="ve-globe__fallback">
      <div className="ve-globe__fallback-disc" aria-hidden="true" />
      <p>
        Your browser does not support 3D graphics, so the interactive globe is hidden.
        The destinations are listed in full alongside.
      </p>
    </div>
  );
}
